import './SegmentedControl.css';
import { LiveDot } from './LiveDot';

export type SegmentedVariant = 'capsule' | 'loose';

interface OptionBase<Id extends string> {
  id: Id;
  label: string;
  disabled?: boolean;
  /** Dica nativa; obrigatória na prática quando `disabled` (explica por quê, ex.: "Candidato" no replay, §9.3). */
  title?: string;
  /** `aria-keyshortcuts` do botão (ex.: "1" nas abas de cargo, §5.7). */
  keyshortcut?: string;
}

/**
 * Opção do grupo. O badge `live` põe um `LiveDot` (`blink`) decorativo no canto e acrescenta `badgeLabel` ao nome
 * acessível (ex.: ", em apuração" → "Governadores, em apuração", §6.3); o texto vem do dono. O nome vai num
 * `aria-label` que começa pelo rótulo visível (2.5.3), e não num `.sr-only` irmão: com o `.sr-only`, o cálculo de
 * nome pode separar os pedaços com espaço ("Governadores , em apuração"); o `aria-label` o torna determinístico.
 */
export type SegmentedOption<Id extends string = string> = OptionBase<Id> &
  ({ badge?: undefined; badgeLabel?: undefined } | { badge: 'live'; badgeLabel: string });

export interface SegmentedControlProps<Id extends string = string> {
  options: ReadonlyArray<SegmentedOption<Id>>;
  /** Opção selecionada; `null` = nenhuma. */
  value: Id | null;
  onChange: (id: Id) => void;
  /** Nome do grupo (ex.: "Cargo", "O mapa mostra"). */
  ariaLabel: string;
  variant?: SegmentedVariant;
  /** TV: só indica (botões com `tabindex="-1"` e `pointer-events: none`, §3.8). */
  readOnly?: boolean;
  /** Classes extras do dono. */
  class?: string;
}

/**
 * Grupo de botões exclusivos (§3.8): `role="group"` com `<button aria-pressed>` independentes, um por parada de Tab;
 * não é `tablist` (sem setas). Com menos de 2 opções, não renderiza nada (AC-3.8-12).
 */
export function SegmentedControl<Id extends string = string>({
  options,
  value,
  onChange,
  ariaLabel,
  variant = 'capsule',
  readOnly = false,
  class: extra,
}: SegmentedControlProps<Id>) {
  if (options.length < 2) return null;
  return (
    // biome-ignore lint/a11y/useSemanticElements: §3.8 e §6.3 pedem <div role="group">; <fieldset> não aceita inline-flex de forma confiável e exige <legend>
    <div
      role="group"
      aria-label={ariaLabel}
      class={`segmented-control segmented-control--${variant}${extra ? ` ${extra}` : ''}`}
      data-testid="segmented-control"
      data-state={readOnly ? 'readonly' : undefined}
    >
      {options.map((o) => {
        const selected = o.id === value;
        const off = o.disabled === true;
        const live = o.badge === 'live';
        return (
          <button
            key={o.id}
            type="button"
            class="segmented-control__item"
            data-id={o.id}
            aria-pressed={selected ? 'true' : 'false'}
            aria-disabled={off ? 'true' : undefined}
            aria-keyshortcuts={o.keyshortcut}
            aria-label={live ? `${o.label}${o.badgeLabel}` : undefined}
            title={o.title}
            tabIndex={readOnly ? -1 : undefined}
            onClick={() => {
              if (off || readOnly || selected) return;
              onChange(o.id);
            }}
          >
            {o.label}
            {live && (
              <span class="segmented-control__badge" aria-hidden="true">
                <LiveDot variant="blink" />
              </span>
            )}
          </button>
        );
      })}
    </div>
  );
}
